import { OutreachStatus } from "@plenum/shared/api";
import { useState } from "react";
import {
  ApiError,
  useCreateNote,
  useCreateOutreach,
  useDeleteNote,
  useNotes,
  useOutreach,
} from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { Pill } from "../Badge";
import { useToast } from "../Toast";
import { ComposePanel } from "./ComposePanel";

/** Activity (PRD §12.3 tab 6): org-scoped outreach events and notes for this site. */
export function ActivityTab({ siteId }: { siteId: number }) {
  const events = useOutreach({ site_id: siteId });
  const notes = useNotes("site", siteId);
  const addEvent = useCreateOutreach();
  const addNote = useCreateNote();
  const delNote = useDeleteNote();
  const toast = useToast();
  const [status, setStatus] = useState<(typeof OutreachStatus.options)[number]>("contacted");
  const [channel, setChannel] = useState<"email" | "phone" | "meeting" | "linkedin" | "other">(
    "email",
  );
  const [eventNote, setEventNote] = useState("");
  const [note, setNote] = useState("");

  const fail = (e: unknown) =>
    toast(e instanceof ApiError ? e.message : "Request failed", "danger");

  return (
    <div className="flex flex-col gap-4">
      <ComposePanel siteId={siteId} />
      <section>
        <h3 className="eyebrow mb-1">Log outreach</h3>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await addEvent.mutateAsync({
                site_id: siteId,
                status,
                channel,
                note: eventNote.trim() || undefined,
              });
              setEventNote("");
              toast(`Logged ${status}`);
            } catch (err) {
              fail(err);
            }
          }}
        >
          <select
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            aria-label="Outreach status"
            data-testid="outreach-status"
          >
            {OutreachStatus.options.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            className="input"
            value={channel}
            onChange={(e) => setChannel(e.target.value as typeof channel)}
            aria-label="Channel"
          >
            {["email", "phone", "meeting", "linkedin", "other"].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            className="input flex-1"
            value={eventNote}
            onChange={(e) => setEventNote(e.target.value)}
            placeholder="What happened"
            aria-label="Event note"
            data-testid="outreach-note"
          />
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={addEvent.isPending}
            data-testid="outreach-submit"
          >
            Log
          </button>
        </form>
        <ul className="mt-2 flex flex-col gap-1" data-testid="outreach-events">
          {(events.data?.items ?? []).map((ev) => (
            <li key={ev.id} className="flex flex-wrap items-center gap-2 text-[12px]">
              <span className="num" style={{ color: "var(--muted-2)" }}>
                {fmtDate(ev.occurred_at)}
              </span>
              <Pill
                tone={
                  ev.status === "won"
                    ? "ok"
                    : ev.status === "lost" || ev.status === "dnc"
                      ? "danger"
                      : "gold"
                }
              >
                {ev.status}
              </Pill>
              {ev.channel ? <span style={{ color: "var(--muted)" }}>{ev.channel}</span> : null}
              <span>{ev.note}</span>
              <span style={{ color: "var(--muted-2)" }}>{ev.user_email}</span>
            </li>
          ))}
          {events.data && events.data.items.length === 0 ? (
            <li style={{ color: "var(--muted)" }}>No outreach yet.</li>
          ) : null}
        </ul>
      </section>
      <section>
        <h3 className="eyebrow mb-1">Notes</h3>
        <form
          className="flex gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!note.trim()) return;
            try {
              await addNote.mutateAsync({
                entity_type: "site",
                entity_id: siteId,
                body: note.trim(),
              });
              setNote("");
            } catch (err) {
              fail(err);
            }
          }}
        >
          <textarea
            className="input h-16 flex-1"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Internal note (org-scoped)"
            aria-label="Note"
            data-testid="note-body"
          />
          <button
            type="submit"
            className="btn btn-sm self-end"
            disabled={addNote.isPending || !note.trim()}
            data-testid="note-submit"
          >
            Add note
          </button>
        </form>
        <ul className="mt-2 flex flex-col gap-2" data-testid="notes">
          {(notes.data?.items ?? []).map((n) => (
            <li
              key={n.id}
              className="rounded border p-2 text-[12px]"
              style={{ borderColor: "var(--line)", background: "var(--panel-2)" }}
            >
              <div className="flex items-center gap-2" style={{ color: "var(--muted-2)" }}>
                <span className="num">{fmtDate(n.created_at)}</span>
                <span>{n.user_email}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm ml-auto"
                  onClick={() => delNote.mutateAsync(n.id).catch(fail)}
                  aria-label="Delete note"
                >
                  delete
                </button>
              </div>
              <p className="mt-1 whitespace-pre-wrap">{n.body}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
